import i18n from './index';
import { fixedUi } from './fixedUi';
import { localizedColorLabel } from './colorNameText';
import { localizedShopTypeLabel } from './shopTypeText';

/** 发布记录等列表里存的是中文状态/类型/站点，展示时换成当前语言。 */

const RECORD_LABEL_KEYS = {
  待审核: 'publishRecord.tabPendingReview',
  已通过: 'publishRecord.tabApproved',
  已上架: 'publishRecord.tabListed',
  已上市: 'publishRecord.tabListed',
  已下架: 'tiktokCatalog.offline',
  已驳回: 'publishRecord.tabRejected',
  待商家确认: 'publishRecord.tabMerchant',
  处理中: 'publishRecord.tabProcessing',
  接受失败: 'publishRecord.tabAcceptFailed',
  已撤回: 'publishRecord.tabWithdrawn',
  申诉中: 'publishRecord.tabAppeal',
  新增: 'publishRecord.sourceNew',
  编辑: 'publishRecord.sourceEdit',
  同步: 'publishRecord.sourceSync',
  自动上架: 'publishRecord.autoList',
  产品ID: 'publishRecord.productId',
};

const ZH_REGION = {
  美国: 'US',
  墨西哥: 'MX',
  英国: 'GB',
  德国: 'DE',
  法国: 'FR',
  意大利: 'IT',
  西班牙: 'ES',
  爱尔兰: 'IE',
  波兰: 'PL',
  荷兰: 'NL',
  比利时: 'BE',
  印度尼西亚: 'ID',
  印尼: 'ID',
  马来西亚: 'MY',
  泰国: 'TH',
  越南: 'VN',
  菲律宾: 'PH',
  新加坡: 'SG',
  日本: 'JP',
  巴西: 'BR',
  加拿大: 'CA',
  澳大利亚: 'AU',
  澳洲: 'AU',
  新西兰: 'NZ',
  韩国: 'KR',
  瑞典: 'SE',
  葡萄牙: 'PT',
  欧洲: 'EU',
  智利: 'CL',
  哥伦比亚: 'CO',
  秘鲁: 'PE',
  厄瓜多尔: 'EC',
};

function regionCode(raw) {
  let text = String(raw || '').trim();
  if (!text) return '';
  text = text.replace(/^(tiktok|temu|shein)\s*/i, '');
  text = text.replace(/(站点|站)$/, '').trim();
  if (ZH_REGION[text]) return ZH_REGION[text];
  const code = text.toUpperCase() === 'UK' ? 'GB' : text.toUpperCase();
  if (/^[A-Z]{2}$/.test(code)) return code;
  return '';
}

export function localizedMarketText(raw, locale) {
  const text = String(raw ?? '').trim();
  if (!text || text === '—' || text.toUpperCase() === 'ALL') return text;
  const parts = text.split(/[、,，]/).map((part) => part.trim()).filter(Boolean);
  if (parts.length > 1) {
    return parts.map((part) => localizedMarketText(part, locale)).join(', ');
  }
  const code = regionCode(text);
  if (!code || code === 'EU') return text;
  try {
    const name = new Intl.DisplayNames([locale || 'zh-CN'], { type: 'region' }).of(code);
    return name || text;
  } catch {
    return text;
  }
}

/** 站点维度 Tab：SHEIN韩国站 → SHEIN South Korea。 */
export function localizedSiteLabel(raw) {
  const text = String(raw ?? '').trim();
  if (!text) return text;
  const lang = String(i18n.resolvedLanguage || i18n.language || '');
  if (lang.toLowerCase().startsWith('zh')) return text;
  const ui = fixedUi(text);
  if (ui !== text) return ui;
  const market = localizedMarketText(text, lang);
  if (market === text) return text;
  const brand = text.match(/^(shein|temu|tiktok)/i);
  if (!brand) return market;
  const name = brand[1].toLowerCase() === 'tiktok' ? 'TikTok' : brand[1].toUpperCase();
  return `${name} ${market}`;
}

/** 店铺信息页的字段名、状态、站点、店铺类型。 */
export function displayShopFact(raw) {
  const text = raw == null ? '' : String(raw);
  const trimmed = text.trim();
  if (!trimmed || trimmed === '—') return text;
  const lang = String(i18n.resolvedLanguage || i18n.language || '');
  if (lang.toLowerCase().startsWith('zh')) return text;
  const typed = localizedShopTypeLabel(
    (key, opts) => i18n.t(key, { ns: 'workbench', ...opts }),
    trimmed,
  );
  if (typed !== trimmed) return typed;
  return localizedSiteLabel(trimmed);
}

/** 发布记录 SKC 规格里的中文颜色名交给统一的颜色词典翻译。 */
function translateSpecToken(token) {
  return localizedColorLabel(token);
}

/** 发布记录 SKC 规格（颜色等）按当前语言展示。 */
export function displaySpecLabel(raw) {
  const text = String(raw ?? '').trim();
  if (!text || text === '—') return text;
  const lang = String(i18n.resolvedLanguage || i18n.language || '');
  if (lang.toLowerCase().startsWith('zh')) return text;
  return text
    .split(/[/、,，]/)
    .map((part) => translateSpecToken(part.trim()))
    .filter(Boolean)
    .join(' / ');
}

export function localizedRecordLabel(t, raw, locale) {
  const text = String(raw ?? '').trim();
  if (!text) return text;
  const key = RECORD_LABEL_KEYS[text];
  if (key && typeof t === 'function') return t(key, { defaultValue: text });
  return localizedMarketText(text, locale);
}

/** 产地等下拉：用选项上的二字码换成当前语言的地区名。 */
export function localizeRegionOptions(options) {
  const list = Array.isArray(options) ? options : [];
  const lang = String(i18n.resolvedLanguage || i18n.language || '');
  if (lang.toLowerCase().startsWith('zh')) return list;
  let names = null;
  try {
    names = new Intl.DisplayNames([lang], { type: 'region' });
  } catch {
    names = null;
  }
  return list.map((opt) => {
    const code = String(opt?.value || '').trim().toUpperCase();
    if (code === 'OTHER') return { ...opt, label: fixedUi(opt?.label || '其他') };
    const region = code === 'UK' ? 'GB' : code;
    if (names && /^[A-Z]{2}$/.test(region)) {
      const name = names.of(region);
      if (name && name.toUpperCase() !== region) return { ...opt, label: name };
    }
    const market = localizedMarketText(opt?.label, lang);
    if (market && market !== opt?.label) return { ...opt, label: market };
    return { ...opt, label: fixedUi(opt?.label || '') };
  });
}

const CN_PROVINCE_EN = {
  北京市: 'Beijing',
  天津市: 'Tianjin',
  上海市: 'Shanghai',
  重庆市: 'Chongqing',
  河北省: 'Hebei',
  山西省: 'Shanxi',
  辽宁省: 'Liaoning',
  吉林省: 'Jilin',
  黑龙江省: 'Heilongjiang',
  江苏省: 'Jiangsu',
  浙江省: 'Zhejiang',
  安徽省: 'Anhui',
  福建省: 'Fujian',
  江西省: 'Jiangxi',
  山东省: 'Shandong',
  河南省: 'Henan',
  湖北省: 'Hubei',
  湖南省: 'Hunan',
  广东省: 'Guangdong',
  海南省: 'Hainan',
  四川省: 'Sichuan',
  贵州省: 'Guizhou',
  云南省: 'Yunnan',
  陕西省: 'Shaanxi',
  甘肃省: 'Gansu',
  青海省: 'Qinghai',
  内蒙古自治区: 'Inner Mongolia',
  广西壮族自治区: 'Guangxi',
  西藏自治区: 'Tibet',
  宁夏回族自治区: 'Ningxia',
  新疆维吾尔自治区: 'Xinjiang',
};

/** 中国省份下拉。提交值仍是平台 region2Id。 */
export function localizeCnProvinceOptions(options) {
  const list = Array.isArray(options) ? options : [];
  const lang = String(i18n.resolvedLanguage || i18n.language || '');
  if (lang.toLowerCase().startsWith('zh')) return list;
  return list.map((opt) => ({
    ...opt,
    label: CN_PROVINCE_EN[opt?.label] || opt?.label,
  }));
}
